import { computed, ref } from 'vue'
import type nl from '@/locales/nl.json'

export type ToastTone = 'success' | 'error'

export type ToastMessage = Exclude<
  keyof (typeof nl)['toast'],
  | 'saveFailed'
  | 'exportFailed'
  | 'sendFailed'
  | 'copyFailed'
  | 'loadFailed'
  | 'dismiss'
>

export interface Toast {
  id: number
  tone: ToastTone
  message: string
  params?: Record<string, string>
}

const SUCCESS_DURATION_MS = 4000
const ERROR_DURATION_MS = 7000
const MAX_VISIBLE = 3

const toasts = ref<Toast[]>([])
const timers = new Map<
  number,
  { remaining: number; startedAt: number; handle: number }
>()

let nextId = 1

const clearTimer = (id: number) => {
  const timer = timers.get(id)

  if (timer) window.clearTimeout(timer.handle)

  timers.delete(id)
}

const dismiss = (id: number) => {
  clearTimer(id)
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

const startTimer = (id: number, duration: number) => {
  timers.set(id, {
    remaining: duration,
    startedAt: Date.now(),
    handle: window.setTimeout(() => dismiss(id), duration),
  })
}

const pause = (id: number) => {
  const timer = timers.get(id)

  if (!timer) return

  window.clearTimeout(timer.handle)
  timer.remaining -= Date.now() - timer.startedAt
}

const resume = (id: number) => {
  const timer = timers.get(id)

  if (!timer) return

  startTimer(id, Math.max(timer.remaining, 1000))
}

const push = (
  tone: ToastTone,
  message: string,
  params?: Record<string, string>,
) => {
  const id = nextId++

  toasts.value = [...toasts.value, { id, tone, message, params }]

  toasts.value.slice(0, -MAX_VISIBLE).forEach((toast) => dismiss(toast.id))

  startTimer(id, tone === 'error' ? ERROR_DURATION_MS : SUCCESS_DURATION_MS)
}

export const toast = {
  success: (message: ToastMessage, params?: Record<string, string>) =>
    push('success', `toast.${message}`, params),
  saveFailed: () => push('error', 'toast.saveFailed'),
  exportFailed: () => push('error', 'toast.exportFailed'),
  sendFailed: () => push('error', 'toast.sendFailed'),
  copyFailed: () => push('error', 'toast.copyFailed'),
  loadFailed: () => push('error', 'toast.loadFailed'),
}

export const useToast = () => ({
  toasts: computed(() => toasts.value),
  dismiss,
  pause,
  resume,
})
